// Envio de mídia (vídeo e imagem) para o bucket público "oracao-midia".
// O vídeo sobe por XHR para mostrar a porcentagem; a imagem é reduzida no
// navegador antes de subir. Só admin da plataforma consegue escrever no bucket
// (RLS), então usamos o token do usuário logado.

import { supabase } from '@/lib/supabase';
import { reduzirImagem } from '@/lib/imagem';

const BUCKET = 'oracao-midia';
const URL_BASE = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const ANON = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export const LIMITE_VIDEO_MB = 50;
const LIMITE_VIDEO_BYTES = LIMITE_VIDEO_MB * 1024 * 1024;

const TIPOS_VIDEO = ['video/mp4', 'video/quicktime', 'video/webm'];
const TIPOS_IMAGEM = ['image/jpeg', 'image/png', 'image/webp'];

function extensaoVideo(tipo: string): string {
  if (tipo === 'video/quicktime') return 'mov';
  if (tipo === 'video/webm') return 'webm';
  return 'mp4';
}

async function token(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const t = data.session?.access_token;
  if (!t) throw new Error('Sua sessão expirou. Entre de novo para enviar.');
  return t;
}

function mensagemErro(resposta: string): string {
  try {
    const j = JSON.parse(resposta) as { message?: string; error?: string };
    const m = j.message || j.error;
    if (m) return `Não conseguimos enviar agora (${m}).`;
  } catch {
    /* corpo em texto puro */
  }
  return 'Não conseguimos enviar agora. Tente de novo.';
}

function enviar(
  caminho: string,
  corpo: Blob,
  tipo: string,
  onProgresso?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    void (async () => {
      let t: string;
      try {
        t = await token();
      } catch (e) {
        reject(e);
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${URL_BASE}/storage/v1/object/${BUCKET}/${caminho}`);
      xhr.setRequestHeader('authorization', `Bearer ${t}`);
      xhr.setRequestHeader('apikey', ANON);
      xhr.setRequestHeader('x-upsert', 'false');
      xhr.setRequestHeader('cache-control', 'max-age=31536000');
      xhr.setRequestHeader('content-type', tipo);
      if (onProgresso) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgresso(Math.round((e.loaded / e.total) * 100));
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(mensagemErro(xhr.responseText)));
      };
      xhr.onerror = () => reject(new Error('A conexão falhou. Tente de novo.'));
      xhr.send(corpo);
    })();
  });
}

function urlPublica(caminho: string): string {
  return supabase.storage.from(BUCKET).getPublicUrl(caminho).data.publicUrl;
}

export async function enviarVideo(
  arquivo: File,
  dia: string,
  onProgresso?: (pct: number) => void,
): Promise<string> {
  if (arquivo.size > LIMITE_VIDEO_BYTES) {
    throw new Error(`O vídeo é muito grande. O limite é ${LIMITE_VIDEO_MB} MB.`);
  }
  if (!TIPOS_VIDEO.includes(arquivo.type)) {
    throw new Error('Formato não aceito. Use MP4, MOV ou WEBM.');
  }
  const caminho = `videos/${dia}-${Date.now()}.${extensaoVideo(arquivo.type)}`;
  await enviar(caminho, arquivo, arquivo.type, onProgresso);
  return urlPublica(caminho);
}

export async function enviarImagem(arquivo: File, dia: string): Promise<string> {
  if (!TIPOS_IMAGEM.includes(arquivo.type)) {
    throw new Error('Formato não aceito. Use JPG, PNG ou WEBP.');
  }
  const reduzida = await reduzirImagem(arquivo, 1600, 0.85, 'image/jpeg');
  const caminho = `imagens/${dia}-${Date.now()}.jpg`;
  await enviar(caminho, reduzida, 'image/jpeg');
  return urlPublica(caminho);
}

// Reaproveita a leitura do YouTube para a prévia no painel.
export function youtubeId(url: string): { id: string; shorts: boolean } | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\/+/, '');
      return id ? { id, shorts: false } : null;
    }
    if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host.endsWith('.youtube.com')) {
      if (u.pathname === '/watch') {
        const id = u.searchParams.get('v');
        return id ? { id, shorts: false } : null;
      }
      const m = u.pathname.match(/^\/(shorts|embed)\/([^/]+)/);
      if (m) return { id: m[2], shorts: m[1] === 'shorts' };
    }
  } catch {
    /* não é uma URL válida */
  }
  return null;
}